/**
 * My Result page — employee views their own assessment result with full breakdown
 */

import { useState, useEffect } from 'react'
import { useQuery, useQueries } from '@tanstack/react-query'
import { penilaianApi } from '../api/penilaianApi'
import {
  RESULT_STATUS_LABELS,
  RESULT_STATUS_COLORS,
  type AssignmentResponses,
} from '../types/penilaian.types'

type Tab = 'hs' | 'ss' | 'akhlak'

const SS_LABELS: Record<number, string> = {
  4: 'Selalu', 3: 'Sering', 2: 'Kadang-Kadang', 1: 'Tidak Pernah',
}

const ROLE_ORDER: Record<string, number> = { ATASAN: 0, BAWAHAN: 1, PEER: 2, KOMITE: 3 }

function getScoreBand(score: number) {
  if (score >= 90) return { label: 'Sangat Baik', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-300', bar: 'bg-emerald-500' }
  if (score >= 75) return { label: 'Baik',        text: 'text-blue-700',    bg: 'bg-blue-50',    border: 'border-blue-300',    bar: 'bg-blue-500' }
  if (score >= 60) return { label: 'Cukup',       text: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-300',   bar: 'bg-amber-500' }
  return              { label: 'Perlu Perbaikan', text: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-300',     bar: 'bg-red-500' }
}

function ProgressBar({ pct, colorClass = 'bg-blue-500' }: { pct: number; colorClass?: string }) {
  return (
    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden flex-1">
      <div
        className={`h-full rounded-full ${colorClass}`}
        style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
      />
    </div>
  )
}

function ScoreBadge({ score, max }: { score: number | null; max: number }) {
  if (score === null) return <span className="text-gray-300 text-xs">—</span>
  const pct = score / max
  const cls =
    pct >= 0.8 ? 'bg-emerald-100 text-emerald-700' :
    pct >= 0.6 ? 'bg-blue-100 text-blue-700' :
    pct >= 0.4 ? 'bg-amber-100 text-amber-700' :
                 'bg-red-100 text-red-700'
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full tabular-nums ${cls}`}>
      {score.toFixed(1)}
    </span>
  )
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
      fill="none" stroke="currentColor" viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
}

export default function MyResultPage() {
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('')
  const [activeTab, setActiveTab] = useState<Tab>('hs')
  const [openAssessors, setOpenAssessors] = useState<Set<string>>(new Set())

  const toggleAssessor = (id: string) => {
    setOpenAssessors(prev => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id) } else { next.add(id) }
      return next
    })
  }

  const { data: periodsData } = useQuery({
    queryKey: ['penilaian-periods'],
    queryFn: () => penilaianApi.getPeriods(),
  })

  const { data: resultsData, isLoading } = useQuery({
    queryKey: ['my-penilaian-results', selectedPeriodId],
    queryFn: () =>
      penilaianApi.getMyResult(selectedPeriodId ? { period_id: selectedPeriodId } : {}),
  })

  const periods = periodsData?.data ?? []
  const result = (resultsData?.data ?? [])[0] ?? null

  useEffect(() => {
    if (!selectedPeriodId && result?.period_id) {
      setSelectedPeriodId(result.period_id)
    }
  }, [result?.period_id, selectedPeriodId])

  const periodId = result?.period_id ?? ''
  const employeeId = result?.employee_id ?? ''

  const { data: submissionsData } = useQuery({
    queryKey: ['my-submitted-assessments', periodId],
    queryFn: () => penilaianApi.getMySubmittedAssessments({ period_id: periodId }),
    enabled: !!periodId,
  })

  const { data: ssIndicators } = useQuery({
    queryKey: ['ss-indicators'],
    queryFn: () => penilaianApi.getSSIndicators(),
    staleTime: 10 * 60 * 1000,
  })

  const { data: akhlakValues } = useQuery({
    queryKey: ['akhlak-values'],
    queryFn: () => penilaianApi.getAkhlakValues(),
    staleTime: 10 * 60 * 1000,
  })

  const { data: kpiAssignments } = useQuery({
    queryKey: ['my-kpi-assignments', periodId, employeeId],
    queryFn: () => penilaianApi.getEmployeeKPIAssignments(periodId, employeeId),
    enabled: !!periodId && !!employeeId,
  })

  const submissions = (submissionsData?.data ?? []).sort(
    (a, b) => (ROLE_ORDER[a.assessor_role] ?? 9) - (ROLE_ORDER[b.assessor_role] ?? 9)
  )

  const responseQueries = useQueries({
    queries: submissions.map((sub) => ({
      queryKey: ['assignment-responses', sub.id],
      queryFn: () => penilaianApi.getAssignmentResponses(sub.id),
      enabled: !!sub.id,
    })),
  })

  const allResponses: AssignmentResponses[] = responseQueries
    .map((q) => q.data)
    .filter(Boolean) as AssignmentResponses[]

  const SCORE_POINTS: Record<number, number> = { 4: 100, 3: 75, 2: 60, 1: 50 }

  const computeSSScore = (responses: AssignmentResponses | undefined): number | null => {
    if (!responses || !ssIndicators?.length) return null
    let totalWeighted = 0, totalBobot = 0
    for (const ind of ssIndicators) {
      const qIds = ind.questions.map((q: any) => q.id)
      const pts = responses.ss_responses
        .filter((r: any) => qIds.includes(r.ss_question_id))
        .map((r: any) => SCORE_POINTS[r.score as number] ?? 0)
      if (!pts.length) continue
      totalWeighted += (ind.bobot as number) * (pts.reduce((a, b) => a + b, 0) / pts.length)
      totalBobot += ind.bobot as number
    }
    if (totalBobot === 0) return null
    return Math.round(totalWeighted / totalBobot * 10) / 10
  }

  const computeAKHLAKScore = (responses: AssignmentResponses | undefined): number | null => {
    if (!responses || !akhlakValues?.length) return null
    const bobot = 1 / akhlakValues.length
    let totalWeighted = 0, totalBobot = 0
    for (const val of akhlakValues) {
      const bIds = val.behaviors.map((b: any) => b.id)
      const pts = responses.akhlak_responses
        .filter((r: any) => bIds.includes(r.behavior_id))
        .map((r: any) => SCORE_POINTS[r.score as number] ?? 0)
      if (!pts.length) continue
      totalWeighted += bobot * (pts.reduce((a, b) => a + b, 0) / pts.length)
      totalBobot += bobot
    }
    if (totalBobot === 0) return null
    return Math.round(totalWeighted / totalBobot * 10) / 10
  }

  const avgScoreFor = (
    responses: AssignmentResponses[],
    key: 'ss_responses' | 'akhlak_responses',
    idKey: string,
    id: string,
  ): number | null => {
    const scores = responses.flatMap((r) =>
      (r[key] as any[]).filter((s) => s[idKey] === id).map((s) => s.score as number)
    )
    return scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : null
  }

  // Compute live scores from actual submitted responses (mirror backend formula)
  const perAssessorSs = allResponses.map(computeSSScore).filter((s): s is number => s !== null)
  const perAssessorAkhlak = allResponses.map(computeAKHLAKScore).filter((s): s is number => s !== null)
  const liveSsScore = perAssessorSs.length > 0
    ? Math.round(perAssessorSs.reduce((a, b) => a + b, 0) / perAssessorSs.length * 10) / 10
    : null
  const liveAkhlakScore = perAssessorAkhlak.length > 0
    ? Math.round(perAssessorAkhlak.reduce((a, b) => a + b, 0) / perAssessorAkhlak.length * 10) / 10
    : null

  // Use live scores when available; fall back to stored DB value
  const displaySsScore = liveSsScore ?? result?.ss_score ?? null
  const displayAkhlakScore = liveAkhlakScore ?? result?.akhlak_score ?? null
  const displayHsScore = result?.hs_score ?? null
  const displayFinalScore = displayHsScore != null
    ? Math.round(
        (
          (displayHsScore * 0.70) +
          ((displaySsScore ?? 0) * 0.20) +
          ((displayAkhlakScore ?? 0) * 0.10)
        ) * 10
      ) / 10
    : result?.final_score ?? null

  const currentPeriod = periods.find((p) => p.id === selectedPeriodId)
  const hrBand = result?.hr_final_score != null ? getScoreBand(result.hr_final_score) : null
  const band = hrBand ?? (displayFinalScore != null ? getScoreBand(displayFinalScore) : null)

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nilai Penilaian Saya</h1>
          <p className="text-sm text-gray-500 mt-1">Hasil penilaian individu per periode</p>
        </div>

        {/* Period pills */}
        {periods.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-end">
            <button
              onClick={() => setSelectedPeriodId('')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                !selectedPeriodId
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Semua
            </button>
            {periods.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedPeriodId(p.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  selectedPeriodId === p.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Loading ── */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-600" />
        </div>
      )}

      {/* ── Empty state ── */}
      {!isLoading && !result && (
        <div className="text-center py-20 bg-white border border-dashed border-gray-300 rounded-xl">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="font-medium text-gray-500">Belum ada hasil penilaian</p>
          <p className="text-sm text-gray-400 mt-1">
            Hasil tersedia setelah penilaian selesai dan disetujui atasan
          </p>
        </div>
      )}

      {result && (
        <div className="space-y-5">

          {/* ── Summary card ── */}
          <div className={`rounded-2xl border p-6 ${band ? `${band.bg} ${band.border}` : 'bg-white border-gray-200'}`}>
            {/* Top row: period + status */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium text-gray-700">
                  {currentPeriod?.name ?? 'Periode'}
                </span>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${RESULT_STATUS_COLORS[result.approval_status]}`}>
                {RESULT_STATUS_LABELS[result.approval_status]}
              </span>
            </div>

            {/* ── Nilai Resmi HR — hero when set ── */}
            {result.hr_final_score != null && (
              <div className="flex items-center gap-5 mb-5 p-5 bg-blue-600 rounded-2xl">
                <div className="flex-shrink-0 w-24 h-24 rounded-xl bg-white/20 flex flex-col items-center justify-center">
                  <span className="text-4xl font-extrabold text-white tabular-nums leading-none">
                    {result.hr_final_score.toFixed(1)}
                  </span>
                  <span className="text-xs text-blue-200 mt-1">/ 100</span>
                </div>
                <div>
                  <p className="text-xs font-semibold text-blue-200 uppercase tracking-wide">Nilai Resmi HR</p>
                  <p className={`text-2xl font-bold text-white mt-0.5`}>{hrBand?.label}</p>
                  <p className="text-sm text-blue-200 mt-0.5">Ditetapkan oleh HR</p>
                  {displayFinalScore != null && (
                    <p className="text-xs text-blue-300 mt-2">
                      Nilai komputasi: <span className="font-semibold text-white">{displayFinalScore.toFixed(1)}</span>
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ── Computed score hero — only when no HR score ── */}
            {result.hr_final_score == null && displayFinalScore !== null && (
              <div className="flex items-center gap-6 mb-6">
                <div className={`flex-shrink-0 w-24 h-24 rounded-2xl flex flex-col items-center justify-center border-2 bg-white shadow-sm ${band?.border ?? 'border-gray-200'}`}>
                  <span className={`text-3xl font-extrabold tabular-nums leading-none ${band?.text ?? 'text-gray-700'}`}>
                    {displayFinalScore.toFixed(1)}
                  </span>
                  <span className="text-xs text-gray-400 mt-1">/ 100</span>
                </div>
                <div>
                  <div className={`text-lg font-bold ${band?.text ?? 'text-gray-700'}`}>{band?.label}</div>
                  <div className="text-sm text-gray-500 mt-0.5">Nilai Performansi Akhir</div>
                  {result.approval_status !== 'approved' && (
                    <div className="flex items-center gap-1 mt-2">
                      <svg className="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs text-amber-600">Sementara — belum final</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Score breakdown bars ── */}
            {displayFinalScore !== null && (
              <div className="space-y-3 bg-white/60 rounded-xl p-4">
                {[
                  { label: 'Hard Skill', weight: '70%', score: displayHsScore, color: 'bg-blue-500' },
                  { label: 'Soft Skill', weight: '20%', score: displaySsScore, color: 'bg-emerald-500' },
                  { label: 'AKHLAK',     weight: '10%', score: displayAkhlakScore, color: 'bg-purple-500' },
                ].map(({ label, weight, score, color }) => (
                  <div key={label} className="flex items-center gap-3">
                    <div className="w-28 flex-shrink-0">
                      <span className="text-xs font-medium text-gray-700">{label}</span>
                      <span className="text-xs text-gray-400 ml-1.5">{weight}</span>
                    </div>
                    <ProgressBar pct={score ?? 0} colorClass={color} />
                    <span className={`w-10 text-right text-sm font-bold tabular-nums ${
                      score != null && score >= 90 ? 'text-emerald-600' :
                      score != null && score >= 75 ? 'text-blue-600' :
                      score != null && score >= 60 ? 'text-amber-600' :
                      'text-red-500'
                    }`}>
                      {score != null ? score.toFixed(1) : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* ── Empty state ── */}
            {result.hr_final_score == null && displayFinalScore === null && (
              <div className="py-4 text-sm text-gray-400 text-center">
                Nilai belum tersedia — menunggu semua penilaian selesai
              </div>
            )}
          </div>

          {/* ── Tabs ── */}
          {displayFinalScore !== null && (
            <>
              <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden">
                {([
                  { key: 'hs' as Tab,     label: 'Hard Skill',  score: displayHsScore,    color: 'text-blue-600' },
                  { key: 'ss' as Tab,     label: 'Soft Skill',  score: displaySsScore,    color: 'text-emerald-600' },
                  { key: 'akhlak' as Tab, label: 'AKHLAK',      score: displayAkhlakScore, color: 'text-purple-600' },
                ] as const).map(({ key, label, score, color }) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`flex-1 px-4 py-3.5 text-sm font-medium transition-colors flex flex-col items-center gap-0.5 ${
                      activeTab === key
                        ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50/60'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 border-b-2 border-transparent'
                    }`}
                  >
                    <span>{label}</span>
                    {score != null && (
                      <span className={`text-xs font-bold tabular-nums ${activeTab === key ? 'text-blue-600' : color}`}>
                        {score.toFixed(1)}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* ── Hard Skill tab ── */}
              {activeTab === 'hs' && (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  {(kpiAssignments ?? []).length === 0 ? (
                    <div className="p-10 text-center text-sm text-gray-400">
                      Tidak ada KPI assignment untuk periode ini.
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide">
                          <th className="text-left px-5 py-3 font-medium">KPI</th>
                          <th className="text-center px-3 py-3 font-medium w-16">Bobot</th>
                          <th className="text-center px-3 py-3 font-medium w-28">Target</th>
                          <th className="text-center px-3 py-3 font-medium w-28">Realisasi</th>
                          <th className="px-3 py-3 font-medium w-36">Pencapaian</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {(kpiAssignments ?? []).map((kpi) => {
                          const pct = kpi.realisasi_pct
                          const barColor =
                            pct == null ? 'bg-gray-200' :
                            pct >= 100  ? 'bg-emerald-500' :
                            pct >= 75   ? 'bg-blue-500' :
                            pct >= 50   ? 'bg-amber-400' : 'bg-red-400'
                          return (
                            <tr key={kpi.id} className="hover:bg-gray-50/60 group">
                              <td className="px-5 py-3.5">
                                <p className="font-medium text-gray-900">{kpi.kpi?.name}</p>
                                {kpi.kpi?.target_description && (
                                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{kpi.kpi.target_description}</p>
                                )}
                                {kpi.realisasi_comment && (
                                  <p className="text-xs text-blue-600 italic mt-0.5">"{kpi.realisasi_comment}"</p>
                                )}
                              </td>
                              <td className="px-3 py-3.5 text-center">
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-semibold">
                                  {kpi.bobot}%
                                </span>
                              </td>
                              <td className="px-3 py-3.5 text-center text-xs text-gray-600">
                                {kpi.target_value ?? <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-3 py-3.5 text-center text-xs text-gray-700 font-medium">
                                {kpi.realisasi ?? <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-3 py-3.5">
                                {pct != null ? (
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1">
                                        <ProgressBar pct={Math.min(pct, 100)} colorClass={barColor} />
                                      </div>
                                      <span className={`text-xs font-bold tabular-nums w-12 text-right ${
                                        pct >= 100 ? 'text-emerald-600' :
                                        pct >= 75  ? 'text-blue-600' :
                                        pct >= 50  ? 'text-amber-600' : 'text-red-500'
                                      }`}>
                                        {pct.toFixed(1)}%
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-gray-300 text-xs">—</span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                      {(kpiAssignments ?? []).length > 1 && result.hs_score != null && (
                        <tfoot>
                          <tr className="bg-blue-50 border-t-2 border-blue-100">
                            <td className="px-5 py-3 text-sm font-semibold text-blue-800" colSpan={3}>
                              Total Nilai Hard Skill
                            </td>
                            <td colSpan={1} />
                            <td className="px-3 py-3">
                              <div className="flex items-center gap-2">
                                <ProgressBar pct={result.hs_score} colorClass="bg-blue-500" />
                                <span className="text-sm font-bold text-blue-700 tabular-nums w-12 text-right">
                                  {result.hs_score.toFixed(1)}
                                </span>
                              </div>
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  )}
                </div>
              )}

              {/* ── Soft Skill tab ── */}
              {activeTab === 'ss' && (
                <div className="space-y-3">
                  {/* Aggregate card */}
                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <h2 className="font-semibold text-gray-900">Rata-rata Semua Penilai</h2>
                        <p className="text-xs text-gray-400 mt-0.5">{submissions.length} penilai</p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-extrabold text-emerald-600 tabular-nums">
                          {displaySsScore != null ? displaySsScore.toFixed(1) : '—'}
                        </div>
                        <div className="text-xs text-gray-400">/ 100</div>
                      </div>
                    </div>

                    {allResponses.length === 0 ? (
                      <p className="text-sm text-gray-400 italic text-center py-4">Belum ada penilaian.</p>
                    ) : (
                      <div className="space-y-5">
                        {(ssIndicators ?? []).map((indicator) => {
                          const qIds = indicator.questions.map((q: any) => q.id)
                          const allScores = allResponses.flatMap((r) =>
                            r.ss_responses
                              .filter((s: any) => qIds.includes(s.ss_question_id))
                              .map((s: any) => s.score as number)
                          )
                          const indAvg = allScores.length > 0
                            ? allScores.reduce((a, b) => a + b, 0) / allScores.length : null
                          return (
                            <div key={indicator.id}>
                              {/* Indicator header */}
                              <div className="flex items-center gap-3 mb-2">
                                <span className="font-semibold text-gray-800 text-sm">{indicator.name}</span>
                                <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                                  {(indicator.bobot as number * 100).toFixed(0)}%
                                </span>
                                <div className="flex-1 flex items-center gap-2 ml-auto max-w-[160px]">
                                  <ProgressBar pct={indAvg != null ? (indAvg / 4) * 100 : 0} colorClass="bg-emerald-400" />
                                  <ScoreBadge score={indAvg} max={4} />
                                </div>
                              </div>
                              {/* Questions */}
                              <div className="pl-3 border-l-2 border-gray-100 space-y-1.5">
                                {indicator.questions.map((q: any, i: number) => {
                                  const avg = avgScoreFor(allResponses, 'ss_responses', 'ss_question_id', q.id)
                                  return (
                                    <div key={q.id} className="flex items-center gap-3 py-0.5">
                                      <p className="text-xs text-gray-600 flex-1">{i + 1}. {q.question_text}</p>
                                      <div className="flex items-center gap-2 shrink-0 w-36">
                                        <ProgressBar pct={avg != null ? (avg / 4) * 100 : 0} colorClass="bg-emerald-300" />
                                        <div className="flex items-center gap-1 shrink-0">
                                          <ScoreBadge score={avg} max={4} />
                                          {avg !== null && (
                                            <span className="text-xs text-gray-400 hidden sm:inline">{SS_LABELS[Math.round(avg)]}</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  {/* Per-assessor cards */}
                  {submissions.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1 mb-2">
                        Detail per Penilai
                      </p>
                      <div className="space-y-2">
                        {submissions.map((sub, idx) => {
                          const responses = responseQueries[idx]?.data
                          const isOpen = openAssessors.has(sub.id)
                          const ssScore = computeSSScore(responses)
                          return (
                            <div key={sub.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                              <button
                                onClick={() => toggleAssessor(sub.id)}
                                className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
                              >
                                <span className="font-medium text-gray-700 text-sm">Penilai {idx + 1}</span>
                                <div className="flex items-center gap-3">
                                  {ssScore !== null && (
                                    <div className="flex items-center gap-2">
                                      <div className="w-20 hidden sm:block">
                                        <ProgressBar pct={ssScore} colorClass="bg-emerald-400" />
                                      </div>
                                      <span className="text-sm font-bold text-emerald-600 tabular-nums">{ssScore.toFixed(1)}</span>
                                    </div>
                                  )}
                                  <Chevron open={isOpen} />
                                </div>
                              </button>
                              {isOpen && (
                                <div className="px-5 pb-5 pt-3 border-t border-gray-100 space-y-4">
                                  {(ssIndicators ?? []).map((indicator) => {
                                    const qIds = indicator.questions.map((q: any) => q.id)
                                    const indScores = (responses?.ss_responses ?? [])
                                      .filter((r: any) => qIds.includes(r.ss_question_id))
                                      .map((r: any) => r.score as number)
                                    const indAvg = indScores.length > 0
                                      ? indScores.reduce((a, b) => a + b, 0) / indScores.length : null
                                    return (
                                      <div key={indicator.id}>
                                        <div className="flex items-center gap-2 mb-1.5">
                                          <span className="font-medium text-gray-800 text-sm">{indicator.name}</span>
                                          <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                                            {(indicator.bobot as number * 100).toFixed(0)}%
                                          </span>
                                          <div className="ml-auto flex items-center gap-2">
                                            <ScoreBadge score={indAvg} max={4} />
                                            {indAvg !== null && (
                                              <span className="text-xs text-gray-400">{SS_LABELS[Math.round(indAvg)]}</span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="pl-3 border-l-2 border-gray-100 space-y-1">
                                          {indicator.questions.map((q: any, i: number) => {
                                            const resp = (responses?.ss_responses ?? [])
                                              .find((r: any) => r.ss_question_id === q.id)
                                            return (
                                              <div key={q.id} className="flex items-center gap-3 py-0.5">
                                                <p className="text-xs text-gray-600 flex-1">{i + 1}. {q.question_text}</p>
                                                <div className="flex items-center gap-1 shrink-0">
                                                  <ScoreBadge score={resp?.score ?? null} max={4} />
                                                  {resp && (
                                                    <span className="text-xs text-gray-400">{SS_LABELS[resp.score]}</span>
                                                  )}
                                                </div>
                                              </div>
                                            )
                                          })}
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── AKHLAK tab ── */}
              {activeTab === 'akhlak' && (
                <div className="space-y-3">
                  {/* Aggregate card */}
                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <h2 className="font-semibold text-gray-900">Rata-rata Semua Penilai</h2>
                        <p className="text-xs text-gray-400 mt-0.5">{submissions.length} penilai</p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-extrabold text-purple-600 tabular-nums">
                          {displayAkhlakScore != null ? displayAkhlakScore.toFixed(1) : '—'}
                        </div>
                        <div className="text-xs text-gray-400">/ 100</div>
                      </div>
                    </div>

                    {allResponses.length === 0 ? (
                      <p className="text-sm text-gray-400 italic text-center py-4">Belum ada penilaian.</p>
                    ) : (
                      <div className="space-y-5">
                        {(akhlakValues ?? []).map((value) => {
                          const bIds = value.behaviors.map((b: any) => b.id)
                          const allScores = allResponses.flatMap((r) =>
                            r.akhlak_responses
                              .filter((s: any) => bIds.includes(s.behavior_id))
                              .map((s: any) => s.score as number)
                          )
                          const valAvg = allScores.length > 0
                            ? allScores.reduce((a, b) => a + b, 0) / allScores.length : null
                          return (
                            <div key={value.id}>
                              {/* Value header */}
                              <div className="flex items-center gap-3 mb-2">
                                <span className="font-semibold text-gray-800 text-sm">{value.name}</span>
                                <div className="flex-1 flex items-center gap-2 ml-auto max-w-[160px]">
                                  <ProgressBar pct={valAvg != null ? (valAvg / 4) * 100 : 0} colorClass="bg-purple-400" />
                                  <ScoreBadge score={valAvg} max={4} />
                                </div>
                              </div>
                              {/* Behaviors */}
                              <div className="pl-3 border-l-2 border-gray-100 space-y-1.5">
                                {value.behaviors.map((b: any, i: number) => {
                                  const avg = avgScoreFor(allResponses, 'akhlak_responses', 'behavior_id', b.id)
                                  return (
                                    <div key={b.id} className="flex items-center gap-3 py-0.5">
                                      <p className="text-xs text-gray-600 flex-1">{i + 1}. {b.behavior_text}</p>
                                      <div className="flex items-center gap-2 shrink-0 w-36">
                                        <ProgressBar pct={avg != null ? (avg / 4) * 100 : 0} colorClass="bg-purple-300" />
                                        <div className="flex items-center gap-1 shrink-0">
                                          <ScoreBadge score={avg} max={4} />
                                          {avg !== null && (
                                            <span className="text-xs text-gray-400 hidden sm:inline">{SS_LABELS[Math.round(avg)]}</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  {/* Per-assessor cards */}
                  {submissions.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1 mb-2">
                        Detail per Penilai
                      </p>
                      <div className="space-y-2">
                        {submissions.map((sub, idx) => {
                          const responses = responseQueries[idx]?.data
                          const isOpen = openAssessors.has(sub.id + '-akhlak')
                          const akhlakScore = computeAKHLAKScore(responses)
                          return (
                            <div key={sub.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                              <button
                                onClick={() => toggleAssessor(sub.id + '-akhlak')}
                                className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
                              >
                                <span className="font-medium text-gray-700 text-sm">Penilai {idx + 1}</span>
                                <div className="flex items-center gap-3">
                                  {akhlakScore !== null && (
                                    <div className="flex items-center gap-2">
                                      <div className="w-20 hidden sm:block">
                                        <ProgressBar pct={akhlakScore} colorClass="bg-purple-400" />
                                      </div>
                                      <span className="text-sm font-bold text-purple-600 tabular-nums">{akhlakScore.toFixed(1)}</span>
                                    </div>
                                  )}
                                  <Chevron open={isOpen} />
                                </div>
                              </button>
                              {isOpen && (
                                <div className="px-5 pb-5 pt-3 border-t border-gray-100 space-y-4">
                                  {(akhlakValues ?? []).map((value) => {
                                    const bIds = value.behaviors.map((b: any) => b.id)
                                    const valScores = (responses?.akhlak_responses ?? [])
                                      .filter((r: any) => bIds.includes(r.behavior_id))
                                      .map((r: any) => r.score as number)
                                    const valAvg = valScores.length > 0
                                      ? valScores.reduce((a, b) => a + b, 0) / valScores.length : null
                                    return (
                                      <div key={value.id}>
                                        <div className="flex items-center gap-2 mb-1.5">
                                          <span className="font-medium text-gray-800 text-sm">{value.name}</span>
                                          <div className="ml-auto flex items-center gap-1">
                                            <ScoreBadge score={valAvg} max={4} />
                                            {valAvg !== null && (
                                              <span className="text-xs text-gray-400">{SS_LABELS[Math.round(valAvg)]}</span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="pl-3 border-l-2 border-gray-100 space-y-1">
                                          {value.behaviors.map((b: any, i: number) => {
                                            const resp = (responses?.akhlak_responses ?? [])
                                              .find((r: any) => r.behavior_id === b.id)
                                            return (
                                              <div key={b.id} className="py-0.5">
                                                <div className="flex items-center gap-3">
                                                  <p className="text-xs text-gray-600 flex-1">{i + 1}. {b.behavior_text}</p>
                                                  <div className="flex items-center gap-1 shrink-0">
                                                    <ScoreBadge score={resp?.score ?? null} max={4} />
                                                    {resp && (
                                                      <span className="text-xs text-gray-400">{SS_LABELS[resp.score]}</span>
                                                    )}
                                                  </div>
                                                </div>
                                                {resp?.tindakan_aktual && (
                                                  <p className="text-xs text-gray-400 italic mt-0.5 pl-3">"{resp.tindakan_aktual}"</p>
                                                )}
                                              </div>
                                            )
                                          })}
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── HR Section — always visible when approved or HR has set any field ── */}
          {(result.approval_status === 'approved' || result.hr_final_score != null || result.komentar_penilai || result.usulan_pengembangan) && (
            <div className="bg-white border border-blue-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 bg-blue-50 border-b border-blue-100">
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <h2 className="text-sm font-semibold text-blue-700">Dari HR</h2>
              </div>
              <div className="p-5 space-y-4">
                {/* Komentar penilai */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Komentar Penilai</p>
                  {result.komentar_penilai ? (
                    <p className="text-sm text-gray-700 leading-relaxed">{result.komentar_penilai}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">—</p>
                  )}
                </div>

                {/* Usulan pengembangan */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Usulan Pengembangan</p>
                  {result.usulan_pengembangan ? (
                    <p className="text-sm text-gray-700 leading-relaxed">{result.usulan_pengembangan}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">—</p>
                  )}
                </div>

                {/* Employee's own comment */}
                {result.komentar_pekerja && (
                  <div className="pt-3 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Komentar Pekerja</p>
                    <p className="text-sm text-gray-600 leading-relaxed">{result.komentar_pekerja}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
